/**
 * QuickBooks "Sync invoices": choice helpers plus an app-level store that
 * starts a background sync job on the server and polls its progress, so the
 * progress or result is visible when the user leaves Settings and comes back.
 */
import { useSyncExternalStore } from 'react';
import { api } from './api.js';
import { SYNC_CADENCES, getLocalSettings, saveLocalSettings } from './settingsStore.js';

export const LATE_OPTIONS = [
  { value: 0, label: 'Any amount' },
  { value: 30, label: '30+ days' },
  { value: 60, label: '60+ days' },
  { value: 90, label: '90+ days' },
];

/** The user's local calendar date as YYYY-MM-DD. */
export function localIsoDate(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Defaults for the dialog: past due, any amount late. */
export function normalizeSyncChoice(raw) {
  const scope = raw?.scope === 'all' ? 'all' : 'past_due';
  const days = Number(raw?.minDaysOverdue);
  const minDaysOverdue = LATE_OPTIONS.some((o) => o.value === days) ? days : 0;
  return { scope, minDaysOverdue };
}

/** Short label for the result notice, e.g. "Past-due only (30+ days late)". */
export function syncChoiceLabel(choice) {
  const { scope, minDaysOverdue } = normalizeSyncChoice(choice);
  if (scope === 'all') return 'All unpaid invoices';
  return minDaysOverdue ? `Past-due only (${minDaysOverdue}+ days late)` : 'Past-due only';
}

function plural(count, one, many) {
  return `${count} ${count === 1 ? one : many}`;
}

/** Result line after a sync job, one plain sentence per reason. */
export function syncSummaryText(choice, data) {
  const already = data.alreadyImported || 0;
  const noAddress = data.missingAddress || 0;
  const other = data.otherSkipped || 0;
  const parts = [
    data.imported
      ? `${plural(data.imported, 'new demand', 'new demands')} imported.`
      : 'No new invoices to import.',
  ];
  if (data.attachmentsImported) {
    parts.push(
      `${plural(data.attachmentsImported, 'file', 'files')} from QuickBooks ${
        data.attachmentsImported === 1 ? 'was' : 'were'
      } added as evidence.`
    );
  }
  if (already) {
    parts.push(`${plural(already, 'invoice was', 'invoices were')} already imported earlier.`);
  }
  if (noAddress) {
    parts.push(
      `${plural(noAddress, 'invoice was', 'invoices were')} skipped because the customer has no address in QuickBooks.`
    );
  }
  if (other) {
    parts.push(
      `${plural(other, 'invoice', 'invoices')} could not be read and ${other === 1 ? 'was' : 'were'} skipped.`
    );
  }
  if (data.notPastDue) {
    parts.push(
      `${plural(data.notPastDue, 'unpaid invoice is', 'unpaid invoices are')} not past due yet, so ${
        data.notPastDue === 1 ? 'it was' : 'they were'
      } left out.`
    );
  }
  if (data.truncated) {
    parts.push('QuickBooks has more matching invoices than one sync reads. Run Sync again for the rest.');
  }
  return `${syncChoiceLabel(choice)}: ${parts.join(' ')}`;
}

/**
 * Integrations row line for the scheduled sync, e.g.
 * "Auto sync: Weekly · last run Oct 31 · 5 new demands". Null when off.
 */
export function autoSyncRowText(automations, lastAutoSync) {
  if (!automations?.accountingSyncEnabled) return null;
  const cadence =
    SYNC_CADENCES.find((c) => c.value === automations.accountingSyncCadence) ||
    SYNC_CADENCES.find((c) => c.value === 'WEEKLY');
  const head = `Auto sync: ${cadence.label}`;
  if (!lastAutoSync) return `${head} · not run yet`;
  if (lastAutoSync.active) return `${head} · running now`;
  const day = new Date(lastAutoSync.at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  if (lastAutoSync.status !== 'DONE') {
    return `${head} · last run ${day} failed${lastAutoSync.error ? `: ${lastAutoSync.error}` : ''}`;
  }
  const imported = Number(lastAutoSync.imported) || 0;
  return `${head} · last run ${day} · ${
    imported ? plural(imported, 'new demand', 'new demands') : 'no new demands'
  }`;
}

// ---- App-level sync state ----

const IDLE = {
  email: '',
  syncing: false,
  jobId: null,
  progress: null,
  notice: null,
  result: null,
  finishedAt: 0,
  /** Last scheduled (Automations) sync: { at, status, active, imported, error } or null. */
  lastAutoSync: null,
};
let state = IDLE;
const listeners = new Set();

function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Current sync state for `email` (another account's sync is not shown). */
export function useQuickBooksSync(email) {
  const snapshot = useSyncExternalStore(subscribe, () => state);
  const key = String(email || '').toLowerCase();
  return snapshot.email === key ? snapshot : IDLE;
}

/** Hide the last result (tab change, disconnect). A running sync is kept. */
export function clearQuickBooksSyncResult() {
  if (state.syncing) return;
  dismissedJobId = state.jobId;
  setState({ notice: null, result: null });
}

const POLL_MS = 2000;
/** A job that finished while the user was away is shown for this long. */
const RECENT_RESULT_MS = 30 * 60 * 1000;
let pollTimer = null;
let dismissedJobId = null;

function schedulePoll(key) {
  if (pollTimer) return;
  pollTimer = setTimeout(() => {
    pollTimer = null;
    refreshQuickBooksSyncStatus(key);
  }, POLL_MS);
}

function finishedNotice(job) {
  const prefix = job.trigger === 'auto' ? 'Auto sync · ' : '';
  if (job.status === 'DONE') {
    return {
      tone: 'success',
      text: `${prefix}${syncSummaryText(job.filter, job)}`,
      imported: job.imported,
    };
  }
  const base = `${prefix}${job.error || 'QuickBooks invoice sync failed. Please try again.'}`;
  // Imports so far are saved; a new sync skips them and carries on.
  const text = job.imported
    ? `${base} ${plural(job.imported, 'invoice was', 'invoices were')} imported before it stopped. Click Sync invoices again to continue.`
    : base;
  return { tone: 'error', text, imported: job.imported };
}

function applyJob(key, job) {
  if (!job) {
    if (state.email === key && state.syncing) setState({ syncing: false, progress: null });
    return;
  }
  if (job.active) {
    setState({
      email: key,
      syncing: true,
      jobId: job.id,
      progress:
        job.total != null
          ? { processed: job.processed, total: job.total, imported: job.imported }
          : null,
      notice: null,
      result: null,
    });
    schedulePoll(key);
    return;
  }
  const watched = state.email === key && state.syncing && state.jobId === job.id;
  if (!watched) {
    // Already shown, dismissed, or too old to be news.
    const recent = job.finishedAt && Date.now() - Date.parse(job.finishedAt) < RECENT_RESULT_MS;
    if ((state.email === key && state.jobId === job.id) || job.id === dismissedJobId || !recent) {
      return;
    }
  }
  if (job.status === 'DONE') {
    const current = getLocalSettings(key);
    const lastSyncAt = job.finishedAt || new Date().toISOString();
    saveLocalSettings(key, {
      ...current,
      accountingLastSyncAt: lastSyncAt,
      connectedIntegrations: {
        ...(current.connectedIntegrations || {}),
        QUICKBOOKS: {
          ...(current.connectedIntegrations?.QUICKBOOKS || {}),
          status: 'connected',
          lastSyncAt,
        },
      },
    });
  }
  setState({
    email: key,
    syncing: false,
    jobId: job.id,
    progress: null,
    notice: finishedNotice(job),
    result: job.matters?.length
      ? {
          imported: job.imported,
          skipped: job.alreadyImported + job.missingAddress + job.otherSkipped,
          matters: job.matters,
        }
      : null,
    finishedAt: Date.now(),
  });
}

/** Load the latest sync job: shows a running sync, or a result from while away. */
export async function refreshQuickBooksSyncStatus(email) {
  const key = String(email || '').toLowerCase();
  if (!key) return;
  try {
    const data = await api.get('/server/quickbooks_api/sync/status');
    setState({ email: key, lastAutoSync: data?.lastAutoSync || null });
    applyJob(key, data?.job || null);
  } catch {
    // Network blip while a sync runs: keep polling.
    if (state.email === key && state.syncing) schedulePoll(key);
  }
}

/**
 * Start a background sync job on the server. It keeps running if the user
 * leaves the page or closes the tab; progress is polled while visible.
 */
export async function startQuickBooksSync({ email, syncChoice, demandChoices = {} }) {
  if (state.syncing) return;
  const key = String(email || '').toLowerCase();
  const filter = normalizeSyncChoice(syncChoice);
  // Pre-select the same choice next time the Sync dialog opens.
  saveLocalSettings(key, { ...getLocalSettings(key), quickBooksSyncChoice: filter });
  setState({ email: key, syncing: true, jobId: null, progress: null, notice: null, result: null });
  try {
    const data = await api.post('/server/quickbooks_api/sync/invoices', {
      email: key,
      ...demandChoices,
      installmentPlan: demandChoices.Installment_Plan,
      settlementChoice: demandChoices.Settlement_Choice,
      ...filter,
      asOf: syncChoice?.asOf || localIsoDate(),
    });
    applyJob(key, data.job);
  } catch (err) {
    if (err?.code === 'SYNC_RUNNING') {
      // Started from another tab: follow that sync instead.
      await refreshQuickBooksSyncStatus(key);
      return;
    }
    setState({
      syncing: false,
      notice: {
        tone: 'error',
        text: err?.message || 'QuickBooks invoice sync isn’t available yet. Please try again later.',
      },
      finishedAt: Date.now(),
    });
  }
}
